import { FC, useEffect, useMemo, useState } from 'react'

import {
  DevChart,
  DevPanel,
  DevPanelMessage,
  DevPanelToggle
} from '../DevPanel'
import styles from './FpsPanel.module.scss'
import { FpsPanelProps } from './FpsPanel.types'

const MAX_LIFETIME_POINTS = 3600
const COLOR = 'rgb(34,197,94)'

const VIEW_OPTIONS = [
  { value: 'last1min', label: '1 min' },
  { value: 'lifetime', label: 'Lifetime' }
]

type View = 'last1min' | 'lifetime'

function fmt(n: number): string {
  return Math.round(n).toString()
}

export const FpsPanel: FC<FpsPanelProps> = ({ onClose }) => {
  const [history, setHistory] = useState<number[]>([])
  const [view, setView] = useState<View>('last1min')

  useEffect(() => {
    let frameCount = 0
    let lastTime = performance.now()
    let rafId: number

    const tick = (now: number) => {
      frameCount += 1
      const elapsed = now - lastTime
      if (elapsed >= 1000) {
        const fps = Math.round((frameCount * 1000) / elapsed)
        setHistory(prev => {
          const next = [...prev, fps]
          return next.length > MAX_LIFETIME_POINTS
            ? next.slice(-MAX_LIFETIME_POINTS)
            : next
        })
        frameCount = 0
        lastTime = now
      }
      rafId = requestAnimationFrame(tick)
    }

    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [])

  const displayedHistory = useMemo(
    () => (view === 'last1min' ? history.slice(-60) : history),
    [history, view]
  )

  const latest = history[history.length - 1]

  const avg = useMemo(() => {
    if (displayedHistory.length === 0) return null
    const sum = displayedHistory.reduce((a, b) => a + b, 0)
    return Math.round(sum / displayedHistory.length)
  }, [displayedHistory])

  return (
    <DevPanel
      title='FPS'
      defaultX={window.innerWidth - 400}
      defaultY={390}
      currentValue={
        latest != null ? (
          <span className={styles.currentValue}>
            {fmt(latest)}
            <span className={styles.currentUnit}> fps</span>
          </span>
        ) : undefined
      }
      actions={
        <DevPanelToggle
          options={VIEW_OPTIONS}
          value={view}
          onChange={v => setView(v as View)}
        />
      }
      onClose={onClose}
      footer={
        latest != null ? (
          <>
            <span>Current: {fmt(latest)} fps</span>
            {avg != null && <span>Avg: {fmt(avg)} fps</span>}
          </>
        ) : undefined
      }
    >
      {displayedHistory.length < 2 ? (
        <DevPanelMessage>Collecting data…</DevPanelMessage>
      ) : (
        <DevChart
          data={displayedHistory}
          totalPoints={history.length}
          color={COLOR}
          unit='fps'
          formatValue={fmt}
        />
      )}
    </DevPanel>
  )
}
